// frontend/src/pages/ProgressWidgets.js — NEW FILE (Phase G)
// Shared between the mobile reader/feed AND the desktop screen:
//   reportProgress()  — fire-and-forget save (status / confidence / flag)
//   ConfidenceStrip   — "How well did you understand this?" 😕 🙂 😎
//   FlagChip          — tap to cycle: none → Important → Difficult → Revision → none
// A "card" is a subtopic/chunk/micro_chunk CardItem.

import React, { useState, useEffect } from 'react';
import { API_BASE, authFetch } from '../services/api.js';
import { getStudentId } from './AskAiPanel';
import './studentfeed.css';

export const reportProgress = (topicId, card, fields) => {
    const sid = getStudentId();
    if (!sid || !card || !topicId) return;
    authFetch(`${API_BASE}/progress/card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            student_id: sid,
            topic_id: topicId,
            card_type: card.card_type,
            card_id: card.card_id,
            ...fields,
        }),
    }).catch(() => {});
};

// ── Confidence strip ─────────────────────────────────────────────
const CONF_LABELS = {
    en: { q: 'How well did you understand this?', low: 'Not clear', ok: 'Okay', high: 'Fully understood', thanks: 'Noted!' },
    hi: { q: 'आपको यह कितना समझ आया?', low: 'साफ नहीं', ok: 'ठीक-ठाक', high: 'पूरी तरह समझ गया', thanks: 'नोट कर लिया!' },
};

export const ConfidenceStrip = ({ topicId, card, language = 'en', initial = null }) => {
    const t = CONF_LABELS[language] || CONF_LABELS.en;
    const [value, setValue] = useState(initial);
    useEffect(() => { setValue(initial); }, [initial, card?.card_type, card?.card_id]);

    const pick = (v) => {
        setValue(v);
        reportProgress(topicId, card, { confidence: v });
    };

    const opts = [
        { v: 'low', emoji: '😕', label: t.low },
        { v: 'ok', emoji: '🙂', label: t.ok },
        { v: 'high', emoji: '😎', label: t.high },
    ];

    return (
        <div className="pgw-confidence">
            <span className="pgw-conf-q">{value ? t.thanks : t.q}</span>
            <div className="pgw-conf-opts">
                {opts.map((o) => (
                    <button
                        key={o.v}
                        className={`pgw-conf-btn ${value === o.v ? 'pgw-conf-active' : ''}`}
                        onClick={() => pick(o.v)}
                    >
                        <span className="pgw-conf-emoji">{o.emoji}</span> {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

// ── Flag chip (cycles on tap) ────────────────────────────────────
export const FLAG_META = {
    important: { icon: '⭐', en: 'Important', hi: 'महत्वपूर्ण' },
    difficult: { icon: '⚠️', en: 'Difficult', hi: 'कठिन' },
    revision: { icon: '🔁', en: 'Revision', hi: 'दोहराना' },
};

export const FlagChip = ({ topicId, card, language = 'en', initial = null }) => {
    const [flag, setFlag] = useState(initial);
    const [open, setOpen] = useState(false);
    useEffect(() => { setFlag(initial); setOpen(false); }, [initial, card?.card_type, card?.card_id]);

    const choose = (v) => {
        const next = flag === v ? null : v;   // tapping the active one clears it
        setFlag(next);
        setOpen(false);
        reportProgress(topicId, card, { flag: next === null ? '__clear__' : next });
    };

    const meta = flag ? FLAG_META[flag] : null;
    const order = ['important', 'difficult', 'revision'];

    return (
        <div className="pgw-flag-wrap">
            <button
                className={`pgw-flag ${flag ? 'pgw-flag-on' : ''}`}
                onClick={() => setOpen((o) => !o)}
                title="Mark this"
            >
                {meta ? `${meta.icon} ${language === 'hi' ? meta.hi : meta.en}` : '🔖'}
            </button>
            {open && (
                <div className="pgw-flag-menu">
                    {order.map((v) => (
                        <button
                            key={v}
                            className={`pgw-flag-opt ${flag === v ? 'pgw-flag-opt-active' : ''}`}
                            onClick={() => choose(v)}
                        >
                            {FLAG_META[v].icon} {language === 'hi' ? FLAG_META[v].hi : FLAG_META[v].en}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};